import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Activity, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export const UptimeMonitorCard = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/monitor/status");
      setS(data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const checkNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/monitor/check");
      setS(data);
      toast[data.status === "up" ? "success" : "error"](
        data.status === "up"
          ? `Site is UP — HTTP ${data.last_status_code} in ${data.last_latency_ms}ms`
          : `Site is DOWN — ${data.last_error || "no response"}`
      );
    } catch {
      toast.error("Check failed to run");
    } finally {
      setBusy(false);
    }
  };

  const up = s?.status === "up";
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

  return (
    <div data-testid="uptime-monitor-card" className="rounded-2xl border border-neutral-800 bg-neutral-950/80 p-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="rounded-lg bg-neutral-900 p-2 text-neutral-400"><Activity size={18} /></span>
          <div>
            <div className="font-bold text-sm uppercase tracking-widest">Uptime Monitor</div>
            <div className="text-xs text-neutral-500 mt-0.5">{s?.url || "…"} · every {Math.round((s?.interval_seconds || 300) / 60)} min · alerts to admin email</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span
            data-testid="uptime-status"
            className={`px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest ${
              s == null || s.status === "unknown"
                ? "bg-neutral-800 text-neutral-400"
                : up
                ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/40"
                : "bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/40"
            }`}
          >
            {s == null || s.status === "unknown" ? "No data yet" : up ? "● Up" : "● Down"}
          </span>
          <button
            data-testid="uptime-check-now-btn"
            onClick={checkNow}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-full border border-neutral-700 text-xs font-bold px-4 py-2 hover:bg-neutral-900 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={13} className={busy ? "animate-spin" : ""} /> Check now
          </button>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
        <div><div className="text-neutral-500">Last check</div><div data-testid="uptime-last-check" className="mt-1 text-neutral-200">{fmt(s?.last_check)}</div></div>
        <div><div className="text-neutral-500">Last OK</div><div className="mt-1 text-neutral-200">{fmt(s?.last_ok)}</div></div>
        <div><div className="text-neutral-500">Response</div><div className="mt-1 text-neutral-200">{s?.last_latency_ms != null ? `${s.last_latency_ms}ms (HTTP ${s.last_status_code})` : "—"}</div></div>
        <div><div className="text-neutral-500">{s?.status === "down" ? "Down since" : "Consecutive fails"}</div><div className="mt-1 text-neutral-200">{s?.status === "down" ? fmt(s?.down_since) : s?.fail_count ?? 0}</div></div>
      </div>
      {s?.last_error && s?.status === "down" && (
        <div className="mt-3 text-xs text-[#FF3B30]" data-testid="uptime-last-error">Last error: {s.last_error}</div>
      )}
    </div>
  );
};
